import React from 'react';
import { Box, Container, Link, Typography } from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import { Link as RouterLink } from 'react-router-dom';
import { FOOTER, LANDING_ASSETS, LANDING_COLORS } from '../../content/landingContent';
import { homeSectionTo, isHomeSectionHash } from '../../utils/landingLinks';

const SOCIAL_ICONS = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  linkedin: LinkedInIcon,
  twitter: XIcon,
};

/** Medium "M" icon — not in MUI set */
const MediumIcon = () => (
  <Typography component="span" sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1, fontFamily: 'serif' }}>
    M
  </Typography>
);

const FooterLink = ({ href, children, internal, external }) => {
  const sx = {
    fontFamily: 'Cabin, sans-serif',
    fontSize: { xs: 13, md: 14 },
    color: LANDING_COLORS.textGray,
    whiteSpace: 'nowrap',
    '&:hover': { color: '#000' },
  };

  if (internal) {
    return (
      <Link component={RouterLink} to={href} sx={sx}>
        {children}
      </Link>
    );
  }

  if (isHomeSectionHash(href)) {
    return (
      <Link component={RouterLink} to={homeSectionTo(href)} sx={sx}>
        {children}
      </Link>
    );
  }

  return (
    <Link
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      sx={sx}
    >
      {children}
    </Link>
  );
};

const Pipe = () => (
  <Typography
    component="span"
    sx={{
      fontFamily: 'Cabin, sans-serif',
      fontSize: { xs: 13, md: 14 },
      color: '#999',
      mx: { xs: 0.75, md: 1 },
    }}
  >
    |
  </Typography>
);

const LandingFooter = () => (
  <Box component="footer" sx={{ bgcolor: '#f5f5f5', py: { xs: 4, md: 5 } }}>
    <Container maxWidth="lg">
      {/* Top row — logo + social icons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: { xs: 3, md: 4 },
        }}
      >
        <Box
          component="img"
          src={LANDING_ASSETS.logo.desktop.scrolled}
          alt="Hype"
          sx={{ height: { xs: 28, md: 32 }, width: 'auto' }}
        />

        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
          {FOOTER.social.map((item) => {
            const Icon = SOCIAL_ICONS[item.icon];
            return (
              <Link
                key={item.label}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={item.label}
                sx={{ color: '#000', display: 'flex', alignItems: 'center', '&:hover': { opacity: 0.7 } }}
              >
                {item.icon === 'medium' ? <MediumIcon /> : <Icon sx={{ fontSize: 20 }} />}
              </Link>
            );
          })}
        </Box>
      </Box>

      {/* Bottom row — nav links + legal */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
          {FOOTER.links.map((link, index) => (
            <React.Fragment key={link.label}>
              {index > 0 && <Pipe />}
              <FooterLink
                href={link.href}
                internal={link.internal}
                external={link.href.startsWith('http')}
              >
                {link.label}
              </FooterLink>
            </React.Fragment>
          ))}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0,
          }}
        >
          <FooterLink href={FOOTER.cookiePolicy.href} external>
            {FOOTER.cookiePolicy.label}
          </FooterLink>
          <Pipe />
          <Typography
            sx={{
              fontFamily: 'Cabin, sans-serif',
              fontSize: { xs: 13, md: 14 },
              color: LANDING_COLORS.textGray,
              whiteSpace: 'nowrap',
            }}
          >
            {FOOTER.copyright}
          </Typography>
        </Box>
      </Box>
    </Container>
  </Box>
);

export default LandingFooter;
